import { PermissionEnum, SecurityLockForm } from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import Steps from '../../../components/new-application/common/steps/steps';
import ProcessingResult from '../../../components/new-application/common/processing-results/processing-result';
import { ApplicationType, ApplicationComment } from '../types';
import { clearLockPanelState } from '../store/slices';
import ApplicationRiskScore from '../../../components/new-application/common/risk-score/risk-score';
import {
  columns,
  columnsLabels,
  filtersSchema,
  logsSchema,
} from './variables/lock-panel';
import AmlChangeLogs from '../../common/aml/parts/change-logs';
import useApplicationTopInfo from './hooks/use-application-info';
import NewApplicationInfoTopControls from './parts/top-controls';
import NewApplicationTopBaseInfo from './parts/base-info';
import NewApplicationInfoBottom from './parts/bottom-info';
import ApplicationMatches from './parts/matches';
import NewApplicationTopPopups from './parts/top-popups';
import NewApplicationTopSidepanels from './parts/top-sidepanels';
import { NewApplicationInfoTopTypes } from './types';

import cn from './styles.module.css';

const NewApplicationInfoTop = ({
  setMatchSelected,
  onSubmit,
  onCreate,
  createInProgress,
  stepsStatus,
  applicationId,
  isCorporate,
  requestInProgress,
  fields,
  defaultFormValues,
  isCompanyOfficial,
  applicationType,
  isEditable,
  createAvailable,
  canSelectResponsible,
  title,
  statusChangeError,
  applicationChat,
  riskScore,
  setError,
  clearErrors,
  questionnarieFields,
  defaultQuestionnarieFormValues,
  customersSuppliersStepEnabled,
  questionnarieSetError,
  questionnarieClearErrors,
  changeLogs,
  changeLogsPagination,
  systemLogs,
  getApplicationDocuments,
  amlUpdateStatus,
  totalIncomeError,
  onSaveAccountsCurrencies,
  accountsCurrenciesValues,
  accountsCurrencies,
  onSaveAccountsFlows,
  accountsFlowsValues,
  accountsFlows,
  onSaveExpectedFundsOrigins,
  expectedFundsOriginsValues,
  expectedFundsOrigins,
  stepOneBlockLoadingInProgress,
  onSaveCrsAndFatca,
  crsAndFatcaValues,
  crsAndFatca,
  onSaveAffilatedCompany,
  affilatedCompaniesValues,
  affilatedCompanies,
  percentageError,
  setHaveSecondPassport,
  haveSecondPassport,
  newApplicationInfo,
  setSuccessMessageVisible,
  setStatusChangeError,
  updateQuestionnarieError,
  updateError,
  infoDictionariesIsLoading,
}: NewApplicationInfoTopTypes) => {
  const dispatch = useAppDispatch();

  const {
    currentEmployee,
    lockItem,
    lockItemAuditLogs,
    lockItemAuditLogsPagination,
    amlChangeLogs,
    amlChangeLogsPagination,
  } = useAppSelector((state) => state.newApplications.list);
  const { riskScoreRanges, employeesList } = useAppSelector(
    (state) => state.customersDictionaries,
  );
  const { users } = useAppSelector((state) => state.administrationDictionaries);
  const { users: employees } = useAppSelector((state) => state.userList.users);
  const { activePermissions } = useAppSelector((state) => state.root);

  const {
    setActionMenuVisibile,
    actionMenuVisible,
    submitIsLoading,
    udpateInProgress,
    udpateCofInProgress,
    udpateCorporateInProgress,
    updateCofQuestionnarieInProgress,
    updateIndividualQuestionnarieInProgress,
    updateCorporateQuestionnarieInProgress,
    onSubmitApplicationAction,
    setRejectNotifyPopupVisible,
    submitError,
    globalError,
    questionnarieGlobalError,
    setResponsiblePopupVisible,
    setWithStatusChange,
    setUserSidepanelVisible,
    setChangeLogsPanelIsOpen,
    onIDCopy,
    idCopySuccess,
    riskScoreRequestIsSuccess,
    riskScoreRequestInProgress,
    riskScoreIsLoading,
    onUpdate,
    setRiskScoreChangeLogsSidepanelVisible,
    setManualScoreSidepanelVisible,
    duplicatesCollapsed,
    setDuplicatesCollapsed,
    textareaIsVisible,
    setTextareaIsVisible,
    rejectNotifyPopupVisible,
    withStatusChange,
    setReviewEmployeeAction,
    selectedResponsible,
    setReviewEmployeeInProgress,
    responsiblePopupVisible,
    setSelectedResponsible,
    changeLogsPanelIsOpen,
    activeTab,
    setActiveTab,
    changeLogsIsLoading,
    changeLogsIsFetching,
    getChangeLogsAction,
    userSidepanelVisible,
    setUserDetailsActiveTab,
    userDetailsActiveTab,
    getLocks,
    activeStatus,
    updateClientInProgress,
    onUpdateClientStatus,
    recoveryInProgress,
    onResetPassword,
    getListAuditLogs,
    auditLogsIsFetching,
    auditLogsIsLoading,
    setLockId,
    setLockFormVisible,
    locksIsLoading,
    getLockItem,
    create,
    update,
    locksIsFetching,
    isSuccess,
    lockItemIsLoading,
    lockItemIsFetching,
    createLockRequestInProgress,
    updateInProgress,
    lockId,
    lockFormVisible,
    getLockItemLogsList,
    lockItemLogsIsLoading,
    lockItemLogsIsFetching,
    setResetPopupVisible,
    resetPopupUpVisible,
    resetCopyIsSuccess,
    link,
    token,
    onResetCopy,
    manualScoreSidepanelVisible,
    setTotalRiskScore,
    setTotalRiskScoreLevel,
    setManualRiskScoreError,
    manualRiskScoreError,
    createRiskScoreIsLoading,
    createApplicationManualRiskScoreAction,
    totalRiskScoreLevel,
    totalRiskScore,
    riskScoreChangeLogsSidepanelVisible,
    handleCloseAmlChangeLogsPanel,
    getApplicationsAmlScoresHistoryAction,
    amlScoresHistoryIsLoading,
    amlScoresHistoryIsFetching,
    backToIncompleteInProgress,
    setPopupVisible,
    onBackToIncomplete,
    onAddResponsible,
    addResponsibleInProgress,
    onRejectAplication,
    rejectInProgress,
    popupVisible,
    addCommentAction,
    sendCommentInProgress,
  } = useApplicationTopInfo({
    applicationId,
    clearErrors,
    questionnarieClearErrors,
    isCorporate,
    isCompanyOfficial,
    fields,
    questionnarieFields,
    onSaveAccountsCurrencies,
    onSaveAccountsFlows,
    onSaveExpectedFundsOrigins,
    onSaveCrsAndFatca,
    onSaveAffilatedCompany,
    setHaveSecondPassport,
    haveSecondPassport,
    setError,
    questionnarieSetError,
    changeLogs,
    activePermissions,
    setStatusChangeError,
    currentEmployee,
    newApplicationInfo,
    amlUpdateStatus,
    riskScore,
    setSuccessMessageVisible,
    riskScoreRanges,
    getApplicationDocuments,
  });

  return (
    <>
      <div className={cn.newApplicationTop}>
        <div className={cn.newApplicationTopInfo}>
          <NewApplicationInfoTopControls
            newApplicationInfo={newApplicationInfo}
            title={title}
            applicationId={applicationId}
            isEditable={isEditable}
            requestInProgress={requestInProgress}
            onSubmit={onSubmit}
            fields={fields}
            defaultFormValues={defaultFormValues}
            questionnarieFields={questionnarieFields}
            defaultQuestionnarieFormValues={defaultQuestionnarieFormValues}
            accountsCurrenciesValues={accountsCurrenciesValues}
            accountsCurrencies={accountsCurrencies}
            accountsFlowsValues={accountsFlowsValues}
            accountsFlows={accountsFlows}
            crsAndFatcaValues={crsAndFatcaValues}
            crsAndFatca={crsAndFatca}
            expectedFundsOriginsValues={expectedFundsOriginsValues}
            expectedFundsOrigins={expectedFundsOrigins}
            affilatedCompaniesValues={affilatedCompaniesValues}
            affilatedCompanies={affilatedCompanies}
            totalIncomeError={totalIncomeError}
            percentageError={percentageError}
            setActionMenuVisibile={setActionMenuVisibile}
            actionMenuVisible={actionMenuVisible}
            backToIncompleteInProgress={backToIncompleteInProgress}
            submitIsLoading={submitIsLoading}
            udpateInProgress={udpateInProgress}
            udpateCofInProgress={udpateCofInProgress}
            udpateCorporateInProgress={udpateCorporateInProgress}
            updateCofQuestionnarieInProgress={updateCofQuestionnarieInProgress}
            updateIndividualQuestionnarieInProgress={
              updateIndividualQuestionnarieInProgress
            }
            updateCorporateQuestionnarieInProgress={
              updateCorporateQuestionnarieInProgress
            }
            stepOneBlockLoadingInProgress={stepOneBlockLoadingInProgress}
            activePermissions={activePermissions}
            onSubmitApplicationAction={onSubmitApplicationAction}
            setRejectNotifyPopupVisible={setRejectNotifyPopupVisible}
            setPopupVisible={setPopupVisible}
            onBackToIncomplete={onBackToIncomplete}
            submitError={submitError || updateError}
            globalError={globalError}
            questionnarieGlobalError={
              questionnarieGlobalError || updateQuestionnarieError
            }
            isCorporate={isCorporate}
            isCompanyOfficial={isCompanyOfficial}
            stepsStatus={stepsStatus}
            createAvailable={createAvailable}
            createInProgress={createInProgress}
            onCreate={onCreate}
            setResponsiblePopupVisible={setResponsiblePopupVisible}
            setWithStatusChange={setWithStatusChange}
            canSelectResponsible={canSelectResponsible}
            infoDictionariesIsLoading={infoDictionariesIsLoading}
          />
          <NewApplicationTopBaseInfo
            newApplicationInfo={newApplicationInfo}
            statusChangeError={statusChangeError}
          />
          <NewApplicationInfoBottom
            setResponsiblePopupVisible={setResponsiblePopupVisible}
            canSelectResponsible={canSelectResponsible}
            newApplicationInfo={newApplicationInfo}
            setUserSidepanelVisible={setUserSidepanelVisible}
            applicationId={applicationId}
            applicationChat={applicationChat}
            setChangeLogsPanelIsOpen={setChangeLogsPanelIsOpen}
            onIDCopy={onIDCopy}
            idCopySuccess={idCopySuccess}
          />
        </div>
      </div>
      {(applicationType === ApplicationType.INDIVIDUAL ||
        applicationType === ApplicationType.CORPORATE) &&
        activePermissions?.includes(
          PermissionEnum.APPLICATIONS_CUSTOMERS_SCORE_LIST,
        ) && (
          <ApplicationRiskScore
            aml={riskScore}
            type={applicationType}
            status={newApplicationInfo?.status}
            updateIsSuccess={riskScoreRequestIsSuccess}
            requestInProgress={riskScoreRequestInProgress}
            calculateAllowed={activePermissions?.includes(
              PermissionEnum.APPLICATIONS_CUSTOMERS_SCORE_AUTO_ON_ACTION,
            )}
            manualScoreAllowed={activePermissions?.includes(
              PermissionEnum.APPLICATIONS_CUSTOMERS_SCORE_CREATE_ACTION,
            )}
            amlUpdateStatus={amlUpdateStatus}
            riskScoreIsLoading={riskScoreIsLoading}
            onUpdate={onUpdate}
            users={users}
            changeLogsAvailable={activePermissions?.includes(
              PermissionEnum.APPLICATIONS_CUSTOMERS_SCORE_LIST,
            )}
            setRiskScoreChangeLogsSidepanelVisible={
              setRiskScoreChangeLogsSidepanelVisible
            }
            setManualScoreSidepanelVisible={setManualScoreSidepanelVisible}
          />
        )}
      {newApplicationInfo?.similarApplications?.edges &&
        newApplicationInfo?.similarApplications?.edges.length > 0 && (
          <ApplicationMatches
            applicationType={applicationType}
            applicationId={applicationId}
            newApplicationInfo={newApplicationInfo}
            setMatchSelected={setMatchSelected}
            setDuplicatesCollapsed={setDuplicatesCollapsed}
            duplicatesCollapsed={duplicatesCollapsed}
          />
        )}
      <Steps
        steps={stepsStatus}
        isCorporate={isCorporate}
        isCompanyOfficial={isCompanyOfficial}
        applicationScheme={newApplicationInfo?.applicationScheme}
        customersSuppliersStepEnabled={customersSuppliersStepEnabled}
      />
      <ProcessingResult
        comments={
          newApplicationInfo?.comments?.edges?.map(
            (item) => item?.node,
          ) as ApplicationComment[]
        }
        onCommentSend={(text) => addCommentAction(text)}
        textareaIsVisible={textareaIsVisible}
        setTextareaIsVisible={setTextareaIsVisible}
        applicationId={applicationId}
        sendCommentInProgress={sendCommentInProgress}
      />
      <NewApplicationTopPopups
        setRejectNotifyPopupVisible={setRejectNotifyPopupVisible}
        setTextareaIsVisible={setTextareaIsVisible}
        rejectNotifyPopupVisible={rejectNotifyPopupVisible}
        setResponsiblePopupVisible={setResponsiblePopupVisible}
        setWithStatusChange={setWithStatusChange}
        withStatusChange={withStatusChange}
        setReviewEmployeeAction={setReviewEmployeeAction}
        newApplicationInfo={newApplicationInfo}
        selectedResponsible={selectedResponsible}
        onAddResponsible={onAddResponsible}
        addResponsibleInProgress={addResponsibleInProgress}
        setReviewEmployeeInProgress={setReviewEmployeeInProgress}
        responsiblePopupVisible={responsiblePopupVisible}
        setSelectedResponsible={setSelectedResponsible}
        employeesList={employeesList}
        setPopupVisible={setPopupVisible}
        onRejectAplication={onRejectAplication}
        rejectInProgress={rejectInProgress}
        popupVisible={popupVisible}
        setResetPopupVisible={setResetPopupVisible}
        resetPopupUpVisible={resetPopupUpVisible}
        resetCopyIsSuccess={resetCopyIsSuccess}
        link={link}
        token={token}
        onResetCopy={onResetCopy}
      />
      <NewApplicationTopSidepanels
        changeLogsPanelIsOpen={changeLogsPanelIsOpen}
        globalError={globalError}
        setChangeLogsPanelIsOpen={setChangeLogsPanelIsOpen}
        activePermissions={activePermissions}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        systemLogs={systemLogs}
        applicationId={applicationId}
        changeLogsIsLoading={changeLogsIsLoading}
        changeLogs={changeLogs}
        changeLogsPagination={changeLogsPagination}
        changeLogsIsFetching={changeLogsIsFetching}
        getChangeLogsAction={getChangeLogsAction}
        userSidepanelVisible={userSidepanelVisible}
        setUserSidepanelVisible={setUserSidepanelVisible}
        setUserDetailsActiveTab={setUserDetailsActiveTab}
        userDetailsActiveTab={userDetailsActiveTab}
        applicationType={applicationType}
        getLocks={getLocks}
        newApplicationInfo={newApplicationInfo}
        activeStatus={activeStatus}
        updateClientInProgress={updateClientInProgress}
        onUpdateClientStatus={onUpdateClientStatus}
        recoveryInProgress={recoveryInProgress}
        onResetPassword={onResetPassword}
        getListAuditLogs={getListAuditLogs}
        auditLogsIsFetching={auditLogsIsFetching}
        auditLogsIsLoading={auditLogsIsLoading}
        employees={employees}
        setLockId={setLockId}
        setLockFormVisible={setLockFormVisible}
        locksIsFetching={locksIsFetching}
        locksIsLoading={locksIsLoading}
        manualScoreSidepanelVisible={manualScoreSidepanelVisible}
        setManualScoreSidepanelVisible={setManualScoreSidepanelVisible}
        setTotalRiskScore={setTotalRiskScore}
        setTotalRiskScoreLevel={setTotalRiskScoreLevel}
        setManualRiskScoreError={setManualRiskScoreError}
        manualRiskScoreError={manualRiskScoreError}
        createRiskScoreIsLoading={createRiskScoreIsLoading}
        createApplicationManualRiskScoreAction={
          createApplicationManualRiskScoreAction
        }
        totalRiskScoreLevel={totalRiskScoreLevel}
        riskScoreRanges={riskScoreRanges}
        totalRiskScore={totalRiskScore}
      />
      <SecurityLockForm
        getLockItem={getLockItem}
        getLocks={getLocks}
        create={create}
        update={update}
        lockItem={lockItem}
        takeCount={20}
        activePermissions={activePermissions}
        employees={employees}
        clearLockPanelState={() => dispatch(clearLockPanelState())}
        onClose={() => {
          setLockFormVisible(false);

          setUserSidepanelVisible(true);
          setLockId('');
        }}
        onBlockUserClick={() => {
          setUserSidepanelVisible(false);
          setLockFormVisible(true);
        }}
        isSuccess={isSuccess}
        isLoading={lockItemIsLoading}
        isFetching={lockItemIsFetching}
        userId={newApplicationInfo?.user?.id}
        requestInProgress={createLockRequestInProgress}
        updateInProgress={updateInProgress}
        lockIdValue={lockId}
        formPanelVisible={lockFormVisible}
        lockItemAuditLogsPagination={lockItemAuditLogsPagination}
        lockItemAuditLogs={lockItemAuditLogs}
        getLockItemLogsList={getLockItemLogsList}
        lockItemLogsSchema={logsSchema}
        lockItemLogsFiltersSchema={filtersSchema}
        lockItemLogsColumns={columns}
        lockItemLogsColumnsLabels={columnsLabels}
        lockItemLogsIsLoading={lockItemLogsIsLoading}
        lockItemLogsIsFetching={lockItemLogsIsFetching}
        withoutUserSelector
      />
      <AmlChangeLogs
        panelIsOpen={riskScoreChangeLogsSidepanelVisible}
        handleCloseAmlChangeLogsPanel={handleCloseAmlChangeLogsPanel}
        getAmlChangeLogs={getApplicationsAmlScoresHistoryAction}
        amlChangeLogsIsLoading={amlScoresHistoryIsLoading}
        amlChangeLogs={amlChangeLogs}
        amlChangeLogsPagination={amlChangeLogsPagination}
        amlScoresHistoryIsFetching={amlScoresHistoryIsFetching}
        users={users}
        type={applicationType}
        applicationId={applicationId}
      />
      <div id="password-popup" />
    </>
  );
};

export default NewApplicationInfoTop;
